import i18nFactory from '@yandex-int/i18n';
import { ExecutionResult } from 'graphql';
import { cloneDeep, get } from 'lodash';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import {
    ADD_RETARGETING_CONDITION_GROUP,
    ADD_RETARGETING_CONDITION_RULE,
    changeRetargetingConditionEditorIsSavingAsNewConditionData,
    changeRetargetingConditionEditorIsSavingData,
    closeRetargetingConditionEditor,
    completeLoadingRetargetingConditionEditorData,
    DELETE_RETARGETING_CONDITION_GROUP,
    DELETE_RETARGETING_CONDITION_RULE,
    OPEN_AUDIENCE_RETARGETING_CONDITION_EDITOR,
    OPEN_NEW_AUDIENCE_RETARGETING_CONDITION_EDITOR,
    OPEN_NEW_RETARGETING_CONDITION_EDITOR,
    OPEN_RETARGETING_CONDITION_EDITOR,
    OpenRetargetingConditionEditor,
    resetVisitorsForecast,
    RetargetingConditionEditorAction,
    SAVE_AUDIENCE_RETARGETING_CONDITION,
    SAVE_RETARGETING_CONDITION,
    SAVE_RETARGETING_CONDITION_AS_NEW,
    saveRetargetingConditionAsNewSuccessed,
    saveRetargetingConditionSuccessed,
    showLoadingRetargetingConditionEditorDataError,
    showRetargetingConditionEditorServerValidationErrors,
    showSavingRetargetingConditionEditorServerGlobalError,
    showVisitorsForecastError,
    UPDATE_RETARGETING_CONDITION_GROUP_TYPE,
    UPDATE_RETARGETING_CONDITION_RULE_GOAL,
    UPDATE_RETARGETING_CONDITION_RULE_PERIOD,
    UPDATE_RETARGETING_CONDITION_RULE_TYPE,
    updateRetargetingConditionEditorGoals,
    updateVisitorsForecast
} from '../../actions/retargetingConditionEditor';
import { updateGroupAudienceRetargetingEditorCondition } from '../../actions/targetings/retargetingConditionsForAudienceEditor';
import { mapRetargetingGoalsFromApiFormat } from '../../mappers/retargeting/retargeting';
import {
    getDomainsFromRulesGroups,
    getGoalsFromRulesGroups,
    hasRetargetingConditionEditorValidationErrors
} from '../../service/RetargetingConditionEditorHelpers/RetargetingConditionEditorHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { UpdateRetargetingConditionsMutation } from '../../typings/graphql-grid';
import { RetargetingConditionDataRulesGroup } from '../../typings/retargeting';
import { State } from '../../typings/state';

import * as keyset from './retargetingConditionEditor.i18n';

const i18n = i18nFactory(keyset);

export const openRetargetingConditionEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store, deps) => {
    return action$
        .ofType(
            OPEN_RETARGETING_CONDITION_EDITOR,
            OPEN_NEW_RETARGETING_CONDITION_EDITOR,
            OPEN_NEW_AUDIENCE_RETARGETING_CONDITION_EDITOR,
            OPEN_AUDIENCE_RETARGETING_CONDITION_EDITOR
        )
        .mergeMap((action: OpenRetargetingConditionEditor) => {
            const {
                commonData: { login }
            } = store.value;

            // Данные по целям загружаем всегда при открытии окна редактирования,
            // хотя можно было бы брать из стейта, если они уже были загружены ранее.
            // Сделано для того, чтобы клиент мог задать новые цели в настройках метрики
            // и увидеть результат не перезагружая страницу.
            return Observable.fromPromise(deps.retargetingDataProvider.getGoalsForRetargetingConditions(login))
                .mergeMap(data => {
                    return Observable.concat(
                        Observable.of(
                            updateRetargetingConditionEditorGoals(
                                mapRetargetingGoalsFromApiFormat(data.client.goals.rowset)
                            )
                        ),
                        Observable.of(completeLoadingRetargetingConditionEditorData())
                    );
                })
                .catch(() =>
                    Observable.of(showLoadingRetargetingConditionEditorDataError(i18n('goals-loading-error')))
                );
        });
};

export const changeVisitorForecastDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store, deps) => {
    return action$
        .ofType(
            DELETE_RETARGETING_CONDITION_GROUP,
            ADD_RETARGETING_CONDITION_GROUP,
            UPDATE_RETARGETING_CONDITION_GROUP_TYPE,
            UPDATE_RETARGETING_CONDITION_RULE_TYPE,
            UPDATE_RETARGETING_CONDITION_RULE_GOAL,
            UPDATE_RETARGETING_CONDITION_RULE_PERIOD,
            DELETE_RETARGETING_CONDITION_RULE,
            ADD_RETARGETING_CONDITION_RULE,
            OPEN_NEW_RETARGETING_CONDITION_EDITOR,
            OPEN_RETARGETING_CONDITION_EDITOR,
            OPEN_NEW_AUDIENCE_RETARGETING_CONDITION_EDITOR,
            OPEN_AUDIENCE_RETARGETING_CONDITION_EDITOR
        )
        .switchMap(
            (): Observable<RetargetingConditionEditorAction> => {
                const {
                    retargetingConditionEditor: {
                        conditionData: { rulesGroups },
                        enableVisitorsForecast
                    }
                } = store.value;

                if (enableVisitorsForecast) {
                    const validRulesGroups = rulesGroups.reduce(
                        (
                            res: Array<RetargetingConditionDataRulesGroup>,
                            rulesGroup: RetargetingConditionDataRulesGroup
                        ) => {
                            const newRulesGroup = cloneDeep(rulesGroup);

                            newRulesGroup.items = newRulesGroup.items.filter(item => item.id !== undefined);

                            if (newRulesGroup.items.length !== 0) {
                                res.push(newRulesGroup);
                            }

                            return res;
                        },
                        []
                    );

                    if (validRulesGroups.length === 0) {
                        return Observable.of(showVisitorsForecastError(i18n('need-some-valid-groups')));
                    }

                    return Observable.of(resetVisitorsForecast()).concat(
                        Observable.fromPromise(
                            deps.retargetingDataProvider.getRetargetingConditionEstimation(validRulesGroups)
                        )
                            .map(({ estimateRetargetingCondition: { estimation, validationResult } }) => {
                                if (validationResult && validationResult.errors.length) {
                                    return showVisitorsForecastError(i18n('visitors-forecast-request-error'));
                                }

                                return updateVisitorsForecast(Number(estimation));
                            })
                            .catch(() => {
                                return Observable.of(
                                    showVisitorsForecastError(i18n('visitors-forecast-request-error'))
                                );
                            })
                    );
                }

                return EMPTY;
            }
        );
};

export const saveRetargetingConditionDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store, deps) => {
    return action$.pipe(
        ofType(SAVE_RETARGETING_CONDITION),
        mergeMap(
            (): Observable<Action> => {
                const {
                    retargetingConditionEditor: { conditionData, goals }
                } = store.value;

                if (hasRetargetingConditionEditorValidationErrors(store.value)) {
                    return of(changeRetargetingConditionEditorIsSavingData(false));
                }

                return from(deps.retargetingMutationExecutor.updateRetargetingCondition(conditionData)).pipe(
                    map(({ errors = [], data }: ExecutionResult<UpdateRetargetingConditionsMutation>) => {
                        if (errors.length === 0) {
                            const validationErrors = get(
                                data,
                                'updateRetargetingConditions.validationResult.errors',
                                []
                            );

                            if (validationErrors.length === 0) {
                                return saveRetargetingConditionSuccessed({
                                    ...conditionData,
                                    domains: getDomainsFromRulesGroups(conditionData.rulesGroups, goals)
                                });
                            } else {
                                return showRetargetingConditionEditorServerValidationErrors(validationErrors);
                            }
                        } else {
                            return showSavingRetargetingConditionEditorServerGlobalError(
                                i18n('saving-condition-general-error')
                            );
                        }
                    }),
                    catchError(() =>
                        of(
                            showSavingRetargetingConditionEditorServerGlobalError(
                                i18n('saving-condition-general-error')
                            )
                        )
                    )
                );
            }
        )
    );
};

export const saveAudienceRetargetingConditionAsNewDataEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store,
    deps
) => {
    return action$.ofType(SAVE_AUDIENCE_RETARGETING_CONDITION).mergeMap((): Observable<Action> | Action[] => {
        const {
            retargetingConditionEditor: { groupId, conditionData, goals }
        } = store.value;

        if (hasRetargetingConditionEditorValidationErrors(store.value)) {
            return of(changeRetargetingConditionEditorIsSavingData(false));
        } else {
            return [
                updateGroupAudienceRetargetingEditorCondition(groupId, {
                    ...conditionData,
                    goals: getGoalsFromRulesGroups(conditionData.rulesGroups, goals)
                }),
                closeRetargetingConditionEditor()
            ];
        }
    });
};

export const saveRetargetingConditionAsNewDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store, deps) => {
    return action$.ofType(SAVE_RETARGETING_CONDITION_AS_NEW).mergeMap(
        (): Observable<Action> => {
            const {
                retargetingConditionEditor: { groupId, conditionData, goals }
            } = store.value;

            if (hasRetargetingConditionEditorValidationErrors(store.value)) {
                return Observable.of(changeRetargetingConditionEditorIsSavingAsNewConditionData(false));
            }

            return Observable.fromPromise(deps.retargetingMutationExecutor.createRetargetingCondition(conditionData))
                .map(({ errors = [], data }) => {
                    if (errors.length === 0) {
                        const validationErrors = get(data, 'createRetargetingConditions.validationResult.errors', []);
                        const updatedConditionId = get(
                            data,
                            'createRetargetingConditions.rowset[0].retargetingConditionId'
                        );

                        if (validationErrors.length === 0 && updatedConditionId) {
                            return saveRetargetingConditionAsNewSuccessed(groupId, {
                                ...conditionData,
                                id: updatedConditionId,
                                domains: getDomainsFromRulesGroups(conditionData.rulesGroups, goals),
                                campaigns: []
                            });
                        } else {
                            return showRetargetingConditionEditorServerValidationErrors(validationErrors);
                        }
                    } else {
                        return showSavingRetargetingConditionEditorServerGlobalError(
                            i18n('saving-condition-general-error')
                        );
                    }
                })
                .catch(() =>
                    Observable.of(
                        showSavingRetargetingConditionEditorServerGlobalError(i18n('saving-condition-general-error'))
                    )
                );
        }
    );
};
